import { useMemo, useState, type FormEvent } from 'react'
import { CheckSquare } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import Select from '@/components/ui/Select'
import SearchableSelect from '@/components/ui/SearchableSelect'
import { useAddTask } from '@/features/tasks/useTasks'
import { useContactSearch, useContacts } from '@/features/contacts/useContacts'
import { useMembers } from '@/features/team/useTeam'
import { useAuthStore } from '@/features/auth/authStore'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { isoInDays } from '@/lib/dealFormat'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import type { Contact } from '@/api/types'
import TimeSelect from '@/components/ui/TimeSelect'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

const nameOf = (c: Pick<Contact, 'firstName' | 'lastName' | 'email'>) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed contact'

/** Quick due-date picks, in days from today. */
const QUICK_DAYS: { label: string; days: number }[] = [
  { label: 'Today', days: 0 },
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
]

/**
 * New task, from anywhere: what to do, when, who it's about (optional) and — for an admin — who does
 * it. Opened from Focus → Tasks ("New task") and from a contact ("Add task", with the client filled in).
 */
export default function NewTaskModal({ contact, onClose }: { contact?: Contact; onClose: () => void }) {
  const add = useAddTask()
  const me = useAuthStore((s) => s.user)
  const isAdmin = me?.role === 'AGENCY_ADMIN'
  const { data: members } = useMembers()

  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState(isoInDays(1))
  const [dueTime, setDueTime] = useState('')
  const [assignee, setAssignee] = useState(me?.agentId ?? '')

  const [clientId, setClientId] = useState(contact?.id ?? '')
  const [picked, setPicked] = useState<{ value: string; label: string } | null>(
    contact ? { value: contact.id, label: nameOf(contact) } : null,
  )
  const [query, setQuery] = useState('')
  const debounced = useDebouncedValue(query.trim(), 300)
  const { data: firstPage } = useContacts()
  const { data: searchPage } = useContactSearch(debounced)
  const options = useMemo(() => {
    const items = (debounced ? searchPage : firstPage)?.items ?? []
    const opts = [{ value: '', label: 'No one in particular' }, ...items.map((c) => ({ value: c.id, label: nameOf(c) }))]
    if (picked && !opts.some((o) => o.value === picked.value)) opts.splice(1, 0, picked)
    return opts
  }, [debounced, searchPage, firstPage, picked])

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    // No time = end of that day, as on a deal's task; the calendar day itself is sent as typed.
    const due = dueDate ? new Date(`${dueDate}T${dueTime || '23:59:59'}`).toISOString() : null
    add.mutate(
      {
        title: title.trim(),
        dueDate: due,
        dueDateLocal: dueDate || null,
        contactId: clientId || null,
        assigneeUserId: isAdmin && assignee ? assignee : null,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      title={contact ? `New task for ${nameOf(contact)}` : 'New task'}
      icon={<CheckSquare size={17} className="text-brand-primary" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
          <button type="submit" form="new-task-form" disabled={!title.trim() || add.isPending} className={modalButton.primary}>
            {add.isPending ? 'Adding…' : 'Add task'}
          </button>
        </>
      }
    >
      <form id="new-task-form" onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">What needs doing?</label>
          <input autoFocus className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Call Sarah about the appraisal" />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">When?</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {QUICK_DAYS.map((q) => (
              <button
                key={q.days}
                type="button"
                onClick={() => setDueDate(isoInDays(q.days))}
                aria-pressed={dueDate === isoInDays(q.days)}
                className={`px-2.5 py-1 text-xs rounded-full border ${dueDate === isoInDays(q.days) ? 'bg-brand-primary border-brand-primary text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-brand-primary/40'}`}
              >
                {q.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" aria-label="Due date" className={inputCls} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            <TimeSelect aria-label="Time (optional)" size="md" value={dueTime} onChange={setDueTime} emptyLabel="Any time" />
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Time is optional — without one it’s due by the end of the day.</p>
        </div>

        {!contact && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">About a client (optional)</label>
            <SearchableSelect
              size="md"
              value={clientId}
              onChange={(id) => {
                setClientId(id)
                setPicked(id ? options.find((o) => o.value === id) ?? null : null)
              }}
              onSearchChange={setQuery}
              placeholder="No one in particular"
              searchPlaceholder="Type a name or email"
              options={options}
              className="w-full"
            />
          </div>
        )}

        {isAdmin && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Who does it?</label>
            <Select size="md" value={assignee} onChange={(e) => setAssignee(e.target.value)} className="w-full">
              {(members ?? []).filter((m) => m.status === 'active' || m.id === assignee).map((m) => (
                <option key={m.id} value={m.id}>{m.id === me?.agentId ? `${m.fullName} (me)` : m.fullName}</option>
              ))}
            </Select>
          </div>
        )}

        {add.isError && <p className="text-danger text-xs">{apiErrorMessage(add.error)}</p>}
      </form>
    </Modal>
  )
}
